import React, { useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ArrowDropDown } from '@material-ui/icons';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import AutocompleteSelect, {
  statusRenderOption,
  statusStartAdornment
} from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';

import { buildStatusReasonItems, DetailsSummary, StatusReasonsSummary } from '../../shared';
import { buildRelatedEntities } from '../../shared/DetailsSummary/DetailsSummary.utils';

import { REASONS_TITLE_BY_STATUS } from './SendoverDetailsTabs/Reasons/Reasons.constants';
import { SwitchSendoverOptions } from './SendoverDetailsDrawer.constants';
import { LoaderContainer, useStyles } from './SendoverDetailsDrawer.styled';
import { SendoverDetailsTabs } from './SendoverDetailsTabs';

const SENDOVER_STATUS_TYPE = 2;
const ACTIVE_SENDOVER_STATUS_ID = 5;

export const SendoverDetailsDrawer = ({
  sendoverId,
  onClose,
  onConvertToSendout,
  onOpenDeclinationReasons = () => {},
  onSetActiveStatus = () => {}
}) => {
  const classes = useStyles();

  const { state, refreshData } = useFetchWithStatusV2({
    endpoint: sendoverId ? `${Endpoints.Sendouts}/${sendoverId}?` : null,
    apiVersion: 1
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const isLoading = state.status === UIStatus.Loading;
  const sendoverStatus = state.results?.status || null;
  const currentStatus = sendoverStatus;
  const declinationReasons = buildStatusReasonItems(state.results?.declination_details);

  const handleStatusChange = async (name, value) => {
    const currentStatusId = sendoverStatus?.id;

    if (value?.id === currentStatusId && value?.id === ACTIVE_SENDOVER_STATUS_ID) {
      return;
    }

    if (value?.id !== ACTIVE_SENDOVER_STATUS_ID) {
      const shouldPreloadReasons =
        currentStatusId !== ACTIVE_SENDOVER_STATUS_ID && value?.id === currentStatusId;

      onOpenDeclinationReasons({
        status: value,
        sendoverId,
        refreshData,
        initialValues: shouldPreloadReasons ? state.results?.declination_details : undefined
      });
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await onSetActiveStatus({ status: value, sendoverId, refreshData });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleConvertToSendout = (event, switchType) => {
    event.preventDefault();
    onConvertToSendout({
      switchType,
      sendover: state.results
    });
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.sendouts.form.buttons.switch,
        disabled: isLoading || isUpdatingStatus || !!state?.error,
        icon: () => <ArrowDropDown />,
        iconPosition: 'right',
        menuItems: SwitchSendoverOptions.map(item => ({
          ...item,
          action: event => handleConvertToSendout(event, item.value)
        }))
      },
      mode: 'menu',
      GridItemProps: { md: 9 }
    }
  ];

  const relatedEntities = buildRelatedEntities(state.results);

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, classes: { paper: classes.drawerLarge } }}
      CardHeaderProps={{ title: 'Sendover Details' }}
      onClose={onClose}
      contentContainerClassName={classes.drawerContentContainer}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 1 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={state.status === UIStatus.Error}>
        <EmptyPlaceholder
          subtitle={strings.sendoutsV2.drawers.sendover.details.error}
          height="100%"
        />
      </When>

      <When condition={isLoading || isUpdatingStatus}>
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      </When>
      <When condition={state.status === UIStatus.Success}>
        <div className={classes.drawerSection}>
          <DetailsSummary
            title={state.results?.joborder?.title}
            primaryAction={
              <AutocompleteSelect
                disableClearable
                name="sendoverStatus"
                onSelect={handleStatusChange}
                placeholder="Status"
                selectedValue={currentStatus}
                getOptionSelected={(option, value) => option?.id === value?.id}
                renderOption={statusRenderOption}
                startAdornment={currentStatus?.color && statusStartAdornment(currentStatus.color)}
                url={`${Endpoints.SendoutsStatus}?type=${SENDOVER_STATUS_TYPE}`}
              />
            }
            relatedEntities={relatedEntities}
          />
          <StatusReasonsSummary
            title={REASONS_TITLE_BY_STATUS[currentStatus?.id]}
            items={declinationReasons}
          />
        </div>
        <SendoverDetailsTabs sendoverId={sendoverId} />
      </When>
    </FPDrawer>
  );
};
